import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import { io } from 'socket.io-client';
import MessageModal from '../common/MessageModal';

const NegotiationPopup = ({ booking, onClose, onConfirmed }) => {
  const navigate = useNavigate();
  const [driverAmount, setDriverAmount] = useState(booking?.negotiation?.currentAmount || null);
  const [userAmount, setUserAmount] = useState('');
  // Initialize status based on booking negotiation state
  const getInitialStatus = () => {
    if (booking?.negotiation?.currentAmount && booking?.negotiation?.proposedBy === 'driver') {
      return 'received'; // Driver has already proposed
    }
    return 'waiting'; // Waiting for driver
  };
  const [status, setStatus] = useState(getInitialStatus());
  const [loading, setLoading] = useState(false);
  const [, setSocket] = useState(null);
  const [otp, setOtp] = useState(null);
  const [isFinalOffer, setIsFinalOffer] = useState(false); // Track if driver's offer is final
  const [hasUserCountered, setHasUserCountered] = useState(false); // Track if user has sent a counter
  
  // State for message modal
  const [messageModal, setMessageModal] = useState({ show: false, message: '', type: 'info', title: 'Message' });
  
  // State to store fetched booking data to ensure we have correct pickupLocation
  const [fetchedBooking, setFetchedBooking] = useState(null);
  const finalBooking = fetchedBooking || booking;

  // Helper function to safely extract OTP code from string or object
  // Prevents React error: "Objects are not valid as a React child"
  const extractOtpCode = (otpValue) => {
    if (!otpValue) return null;
    if (typeof otpValue === 'string' || typeof otpValue === 'number') {
      return String(otpValue);
    }
    if (typeof otpValue === 'object' && otpValue !== null) {
      return otpValue.code ? String(otpValue.code) : null;
    }
    return null;
  };

  // Refs to track latest values for socket callbacks
  const onCloseRef = useRef(onClose);
  const hasUserCounteredRef = useRef(hasUserCountered);

  // Keep refs updated with latest values
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    hasUserCounteredRef.current = hasUserCountered;
  }, [hasUserCountered]);

  // Check if trip has already started - close popup if so
  useEffect(() => {
    // If booking status indicates trip has started, close popup immediately
    const bookingStatus = finalBooking?.status || booking?.status;
    if (['driver_arrived', 'accepted', 'in_progress', 'completed'].includes(bookingStatus)) {
      console.log('🚗 Trip has already started - closing negotiation popup. Status:', bookingStatus);
      if (onClose) {
        onClose();
      }
      return;
    }
  }, [finalBooking?.status, booking?.status, onClose]);

  // Initialize Socket.IO for real-time negotiation
  useEffect(() => {
    const token = localStorage.getItem('uttaapp_token');
    if (!token || (!finalBooking && !booking)) return;

    // Check if trip has already started before setting up socket
    const bookingStatus = finalBooking?.status || booking?.status;
    if (['driver_arrived', 'accepted', 'in_progress', 'completed'].includes(bookingStatus)) {
      console.log('🚗 Trip has already started - not setting up negotiation socket. Status:', bookingStatus);
      if (onCloseRef.current) {
        onCloseRef.current();
      }
      return;
    }

    const newSocket = io(getApiUrl(), {
      auth: { token }
    });

    // Get booking ID (support both id and _id)
    const bookingId = finalBooking?.id || finalBooking?._id || booking?.id || booking?._id;
    if (!bookingId) {
      console.error('❌ NegotiationPopup: No booking ID found');
      return;
    }

    newSocket.on('connect', () => {
      console.log('🔌 User connected to socket for negotiation');
      // Join booking room for real-time updates
      newSocket.emit('join-booking-room', bookingId);
      console.log('📥 User joined booking room:', bookingId);
    });

    // Listen for negotiation updates
    newSocket.on('negotiation-update', (data) => {
      console.log('💰 ===== NEGOTIATION UPDATE RECEIVED =====');
      console.log('💰 Full negotiation data:', JSON.stringify(data, null, 2));
      console.log('💰 Amount received:', data.amount);
      console.log('💰 Proposed by:', data.proposedBy);
      console.log('💰 Status:', data.status);
      
      // IMPORTANT: If trip has started (driver_arrived, accepted, in_progress), close popup immediately
      if (data.status === 'driver_arrived' || data.status === 'in_progress') {
        console.log('🚗 Trip has started - closing negotiation popup. Status:', data.status);
        if (onCloseRef.current) {
          onCloseRef.current();
        }
        return; // Don't process further
      }
      
      if (data.proposedBy === 'driver') {
        // Driver sent an offer - but only process if trip hasn't started
        // Check if this is a status update (trip started) rather than a fare proposal
        if (data.status === 'accepted' && !data.amount) {
          // This is a trip start notification, not a fare proposal
          console.log('🚗 Trip started (accepted status) - closing negotiation popup');
          if (onCloseRef.current) {
            onCloseRef.current();
          }
          return;
        }
        
        console.log('🚗 Driver proposed amount:', data.amount);
        console.log('🚗 Is final offer:', data.isFinalOffer);
        
        if (data.amount) {
          setDriverAmount(parseFloat(data.amount));
          
          // If driver counters after user counter, this is final offer - show Accept/Cancel
          if (data.isFinalOffer && hasUserCounteredRef.current) {
            setIsFinalOffer(true);
            setStatus('final-offer');
            console.log('✅ Driver sent final offer after user counter - showing Accept/Cancel');
          } else {
            // First driver offer or driver's first counter - show Edit/Accept
            setIsFinalOffer(false);
            setStatus('received');
            console.log('✅ Updated driverAmount state to:', data.amount);
            console.log('✅ Updated status to: received (can Edit)');
          }
        } else {
          console.warn('⚠️ Amount is missing in negotiation update');
        }
      } else if (data.proposedBy === 'user') {
        // User sent a counter - update state
        console.log('👤 User sent counter offer');
        setHasUserCountered(true);
      } else if (data.status === 'cancelled') {
        // Negotiation cancelled
        console.log('❌ Negotiation cancelled');
        setStatus('cancelled');
        setMessageModal({
          show: true,
          message: 'Negotiation has been cancelled',
          type: 'info',
          title: 'Negotiation Cancelled'
        });
        if (onCloseRef.current) {
          onCloseRef.current();
        }
      } else if (data.status === 'confirmed' || (data.status === 'accepted' && data.amount)) {
        // Final confirmation (only if it's a fare acceptance, not trip start)
        console.log('✅ Final confirmation received');
        setStatus('confirmed');
      } else {
        console.warn('⚠️ Unknown negotiation update:', data);
      }
    });

    // Listen for OTP
    newSocket.on('otp-generated', (data) => {
      console.log('🔐 OTP received:', data);
      const extractedOtp = extractOtpCode(data.otp);
      if (extractedOtp) {
        setOtp(extractedOtp);
      }
      setStatus('confirmed');
      setDriverAmount(data.fare);
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        const bookingId = finalBooking?.id || finalBooking?._id || booking?.id || booking?._id;
        if (bookingId) {
          newSocket.emit('leave-booking-room', bookingId);
        }
        newSocket.disconnect();
      }
    };
  }, [finalBooking, booking]);

  // Helper function to get booking ID
  const getBookingId = () => {
    return finalBooking?.id || finalBooking?._id || booking?.id || booking?._id || null;
  };

  const handleAcceptAmount = async () => {
    const bookingId = getBookingId();
    if (!bookingId) {
      alert('Booking ID is missing');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        Api.Booking.acceptAmount(bookingId),
        {
          acceptedBy: 'user'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('uttaapp_token')}`
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Driver amount accepted:', response.data);
        console.log('💰 Accepted fare:', response.data.fare);
        console.log('🔐 Generated OTP:', response.data.otp);
        
        const extractedOtp = extractOtpCode(response.data.otp);
        if (extractedOtp) {
          setOtp(extractedOtp);
        }
        setDriverAmount(response.data.fare); // Update displayed amount
        setStatus('confirmed');
        
        // CRITICAL: Close popup permanently after acceptance
        // Don't navigate immediately - let user see confirmation, then close
        if (onConfirmed) {
          onConfirmed({
            fare: response.data.fare,
            otp: response.data.otp,
            status: response.data.status || 'accepted',
            booking: {
              ...response.data.booking,
              // Clear negotiation data to prevent popup from showing again
              negotiation: undefined,
              fare: response.data.fare,
              status: 'accepted'
            }
          });
        }
        
        // Close popup after showing confirmation for 2 seconds
        setTimeout(() => {
          console.log('🚪 Closing negotiation popup permanently after acceptance');
          if (onClose) {
            onClose();
          }
        }, 2000);
        
        // CRITICAL: For reserve bookings, ALWAYS navigate to reserve booking details page after fare acceptance
        // Check multiple sources for bookingType to ensure we catch reserve bookings
        const isReserveBooking = 
          response.data.booking?.bookingType === 'reserve' || 
          booking?.bookingType === 'reserve' ||
          response.data.bookingType === 'reserve';
        
        if (isReserveBooking) {
          console.log('📅 Reserve booking confirmed! Navigating to reserve booking details page...');
          console.log('📅 Booking ID for navigation:', bookingId);
          console.log('📅 Booking type sources:', {
            responseBookingType: response.data.booking?.bookingType,
            bookingType: booking?.bookingType,
            responseBookingType2: response.data.bookingType
          });
          
          // Use bookingId from response if available, otherwise use current bookingId
          const reserveBookingId = response.data.booking?.id || 
                                   response.data.booking?._id || 
                                   response.data.bookingId || 
                                   bookingId;
          
          if (reserveBookingId) {
            setTimeout(() => {
              console.log('📅 Navigating to:', `/reserve-booking/${reserveBookingId}`);
              navigate(`/reserve-booking/${reserveBookingId}`);
            }, 2500); // Give time to show success message and close popup
          } else {
            console.error('❌ Cannot navigate: Reserve booking ID is missing');
          }
        } else {
          console.log('ℹ️ Regular booking - staying on current page');
        }
      }
    } catch (error) {
      console.error('Error accepting amount:', error);
      
      // Show specific error message based on error type
      if (error.response?.status === 409) {
        // Conflict error - driver is busy
        const errorMessage = error.response?.data?.message || 'Driver is already busy with another ride.';
        setMessageModal({
          show: true,
          message: errorMessage + ' Please try again later or find another driver.',
          type: 'warning',
          title: 'Driver Busy'
        });
      } else if (error.response?.status === 403) {
        // Permission denied
        setMessageModal({
          show: true,
          message: 'You do not have permission to accept this amount.',
          type: 'error',
          title: 'Permission Denied'
        });
      } else if (error.response?.status === 404) {
        // Booking not found
        setMessageModal({
          show: true,
          message: 'Booking not found. It may have been cancelled.',
          type: 'warning',
          title: 'Booking Not Found'
        });
      } else {
        // Generic error
        setMessageModal({
          show: true,
          message: error.response?.data?.message || 'Failed to accept amount. Please try again.',
          type: 'error',
          title: 'Error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditAmount = () => {
    setUserAmount(driverAmount?.toString() || '');
    setStatus('editing');
  };

  const handleSendCounterOffer = async () => {
    if (!userAmount || parseFloat(userAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    const bookingId = getBookingId();
    if (!bookingId) {
      alert('Booking ID is missing');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        Api.Booking.proposeAmount(bookingId),
        {
          amount: parseFloat(userAmount),
          proposedBy: 'user'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('uttaapp_token')}`
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Counter offer sent:', response.data);
        setDriverAmount(parseFloat(userAmount));
        setHasUserCountered(true); // Mark that user has countered
        setStatus('user-waiting');
      }
    } catch (error) {
      console.error('Error sending counter offer:', error);
      setMessageModal({
        show: true,
        message: 'Failed to send counter offer. Please try again.',
        type: 'error',
        title: 'Error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNegotiation = async () => {
    const bookingId = getBookingId();
    if (!bookingId) {
      alert('Booking ID is missing');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        Api.Booking.cancelNegotiation(bookingId),
        {
          cancelledBy: 'user'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('uttaapp_token')}`
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Negotiation cancelled');
        setStatus('cancelled');
        setMessageModal({
          show: true,
          message: 'Booking cancelled successfully',
          type: 'success',
          title: 'Booking Cancelled'
        });
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error cancelling negotiation:', error);
      setMessageModal({
        show: true,
        message: 'Failed to cancel booking. Please try again.',
        type: 'error',
        title: 'Error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch full booking data to ensure we have correct pickupLocation (Google Places address)
  useEffect(() => {
    if (!booking) return;

    const bookingId = booking._id || booking.id;
    if (!bookingId) return;

    console.log('📋 NegotiationPopup - Fetching full booking data to ensure correct pickupLocation...', {
      currentPickupLocation: booking.pickupLocation,
      currentPickupLocationName: booking.pickupLocationName
    });

    const fetchFullBooking = async () => {
      try {
        const response = await axios.get(
          Api.Booking.getBooking(bookingId),
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('uttaapp_token')}`
            }
          }
        );

        if (response.data.success && response.data.booking) {
          console.log('✅ NegotiationPopup - Fetched full booking data:', {
            pickupLocation: response.data.booking.pickupLocation,
            pickupLocationName: response.data.booking.pickupLocationName,
            dropoffLocation: response.data.booking.dropoffLocation
          });
          setFetchedBooking(response.data.booking);
        }
      } catch (error) {
        console.error('Error fetching full booking details in NegotiationPopup:', error);
        // Continue with passed booking data if fetch fails
      }
    };

    fetchFullBooking();
  }, [booking]);

  // Debug: Log booking data to check reserve booking fields
  useEffect(() => {
    if (finalBooking) {
      console.log('📋 NegotiationPopup - Booking data:', {
        bookingType: finalBooking.bookingType,
        scheduledStartDate: finalBooking.scheduledStartDate,
        scheduledStartTime: finalBooking.scheduledStartTime,
        scheduledEndDate: finalBooking.scheduledEndDate,
        isReserve: finalBooking.bookingType === 'reserve',
        pickupLocation: finalBooking.pickupLocation,
        pickupLocationName: finalBooking.pickupLocationName
      });
    }
  }, [finalBooking]);

  if (!finalBooking && !booking) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/55 flex items-center justify-center z-50 p-4 backdrop-blur-sm dark:bg-slate-950/80">
      <div className="bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] rounded-[30px] border border-white/70 shadow-[0_32px_90px_-45px_rgba(15,23,42,0.45)] max-w-lg w-full animate-slideUp max-h-[90vh] overflow-y-auto dark:border-slate-700 dark:bg-[linear-gradient(180deg,#0f172a_0%,#111827_100%)]">
        {/* Header */}
        <div className={`${status === 'confirmed' ? 'bg-[linear-gradient(135deg,#16a34a_0%,#15803d_100%)] dark:bg-[linear-gradient(135deg,#166534_0%,#15803d_100%)]' : 'bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_52%,#38bdf8_100%)] dark:bg-[linear-gradient(135deg,#020617_0%,#1e3a8a_52%,#0f766e_100%)]'} p-6 rounded-t-[30px]`}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-white">
              {status === 'confirmed' ? '🎉 Ride Confirmed!' : '💰 Fare Negotiation'}
            </h2>
            {status !== 'confirmed' && (
              <button
                onClick={onClose}
                className="rounded-full border border-white/20 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <p className="text-sm leading-6 text-white/85">
            {status === 'waiting' && 'Waiting for driver to propose fare...'}
            {status === 'received' && 'Driver has proposed a fare'}
            {status === 'final-offer' && 'Driver\'s final offer - Accept or Cancel'}
            {status === 'editing' && 'Propose your fare'}
            {status === 'user-waiting' && 'Waiting for driver response...'}
            {status === 'cancelled' && 'Negotiation cancelled'}
            {status === 'confirmed' && 'Your ride is confirmed!'}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 dark:text-slate-100">
          {/* Driver Info */}
          <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.18)] dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center space-x-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[linear-gradient(135deg,#0f172a_0%,#2563eb_100%)]">
                <span className="text-white font-black text-lg">
                  {(finalBooking?.driverName || booking?.driverName) ? (finalBooking?.driverName || booking.driverName).charAt(0).toUpperCase() : 'D'}
                </span>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-400">Driver</p>
                <p className="text-lg font-bold text-slate-950 dark:text-white">{finalBooking?.driverName || booking?.driverName}</p>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="hidden rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.12)]">
            <div className="space-y-3">
              <div className="flex items-start space-x-2 rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-3">
                <span className="text-green-600 font-semibold">📍</span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Pickup</p>
                  {/* Priority: pickupLocation (Google Places address from "Location Address" input) > pickupLocationName (pincode search from "Location Name" input) */}
                  <p className="mt-1 text-sm font-semibold leading-6 text-slate-900">
                    {(() => {
                      const displayValue = finalBooking?.pickupLocation || finalBooking?.pickupLocationName || booking?.pickupLocation || booking?.pickupLocationName || 'N/A';
                      console.log('📍 NegotiationPopup - Displaying Pickup Location:', {
                        finalBookingPickupLocation: finalBooking?.pickupLocation,
                        finalBookingPickupLocationName: finalBooking?.pickupLocationName,
                        bookingPickupLocation: booking?.pickupLocation,
                        bookingPickupLocationName: booking?.pickupLocationName,
                        displayValue: displayValue,
                        source: (finalBooking?.pickupLocation || booking?.pickupLocation) ? 'pickupLocation (Google Places)' : 'pickupLocationName (pincode search)'
                      });
                      return displayValue;
                    })()}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 rounded-[18px] border border-sky-100 bg-sky-50/70 p-3">
                <span className="text-red-600 font-semibold">📍</span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700">Drop-off</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-slate-900">{finalBooking?.dropoffLocation || booking?.dropoffLocation}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Reserve Booking Schedule Details - Show in all states */}
          {(finalBooking?.bookingType || booking?.bookingType) === 'reserve' && (
            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 dark:border-purple-500/30 dark:bg-slate-800">
              <div className="flex items-center space-x-2 mb-3">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-sm font-bold text-purple-900 dark:text-purple-200">📅 Reserve Booking Schedule</h3>
              </div>
              {((finalBooking?.scheduledStartDate || booking?.scheduledStartDate) || (finalBooking?.scheduledStartTime || booking?.scheduledStartTime) || (finalBooking?.scheduledEndDate || booking?.scheduledEndDate)) ? (
                <div className="space-y-2">
                  {(finalBooking?.scheduledStartDate || booking?.scheduledStartDate) && (
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-600 font-semibold">📆</span>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Scheduled Date</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(finalBooking?.scheduledStartDate || booking.scheduledStartDate).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {(finalBooking?.scheduledStartTime || booking?.scheduledStartTime) && (
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-600 font-semibold">🕐</span>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Scheduled Time</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{finalBooking?.scheduledStartTime || booking.scheduledStartTime}</p>
                      </div>
                    </div>
                  )}
                  {(finalBooking?.scheduledEndDate || booking?.scheduledEndDate) && (
                    <div className="flex items-center space-x-2">
                      <span className="text-purple-600 font-semibold">📆</span>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">End Date</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(finalBooking?.scheduledEndDate || booking.scheduledEndDate).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-600 italic dark:text-slate-400">Schedule details will be updated soon</p>
              )}
            </div>
          )}

          {/* Waiting State */}
          {status === 'waiting' && (
            <div className="text-center py-8">
              <div className="animate-pulse mb-4">
                <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-white">
                Waiting for Driver...
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                The driver is reviewing your booking and will propose a fare soon
              </p>
            </div>
          )}

          {/* Driver Amount Received - First Offer (Edit/Accept) */}
          {status === 'received' && driverAmount && !isFinalOffer ? (
            <>
              <div className="rounded-[28px] border border-amber-300 bg-[linear-gradient(180deg,#fff8e6_0%,#ffffff_100%)] p-7 text-center shadow-[0_28px_70px_-34px_rgba(245,158,11,0.38)] ring-1 ring-amber-100 dark:border-amber-500/30 dark:bg-[linear-gradient(180deg,#3b2a12_0%,#111827_100%)] dark:ring-amber-500/10">
                <h3 className="text-xl font-black tracking-[-0.03em] text-gray-950 mb-2 dark:text-white">
                  Driver's Proposed Fare
                </h3>
                <p className="text-4xl font-bold text-yellow-600 mb-2 dark:text-amber-300">₹{driverAmount}</p>
                <p className="text-base leading-7 text-gray-700 dark:text-slate-300">
                  You can accept this amount or propose a different fare
                </p>
              </div>

              <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.12)] dark:border-slate-700 dark:bg-slate-800">
                <div className="space-y-3">
                  <div className="rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Pickup</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">
                      {(() => {
                        const displayValue = finalBooking?.pickupLocation || finalBooking?.pickupLocationName || booking?.pickupLocation || booking?.pickupLocationName || 'N/A';
                        console.log('NegotiationPopup - Displaying Pickup Location:', {
                          finalBookingPickupLocation: finalBooking?.pickupLocation,
                          finalBookingPickupLocationName: finalBooking?.pickupLocationName,
                          bookingPickupLocation: booking?.pickupLocation,
                          bookingPickupLocationName: booking?.pickupLocationName,
                          displayValue,
                          source: (finalBooking?.pickupLocation || booking?.pickupLocation) ? 'pickupLocation (Google Places)' : 'pickupLocationName (pincode search)'
                        });
                        return displayValue;
                      })()}
                    </p>
                  </div>
                  <div className="rounded-[18px] border border-sky-100 bg-sky-50/70 p-3 dark:border-sky-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Drop-off</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">{finalBooking?.dropoffLocation || booking?.dropoffLocation}</p>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-1 mt-5 border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,251,255,0)_0%,rgba(248,251,255,0.96)_20%,#f8fbff_100%)] px-1 pb-1 pt-4 dark:border-slate-700 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0)_0%,rgba(15,23,42,0.96)_20%,#111827_100%)]">
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={handleEditAmount}
                    className="rounded-[20px] border-2 border-slate-300 bg-white px-6 py-4 text-base font-bold text-slate-800 shadow-[0_16px_34px_-28px_rgba(15,23,42,0.28)] transition-colors hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    disabled={loading}
                  >
                    Edit Amount
                  </button>
                  <button
                    onClick={handleAcceptAmount}
                    className="rounded-[20px] bg-[linear-gradient(135deg,#16a34a_0%,#15803d_100%)] px-6 py-4 text-base font-black text-white shadow-[0_22px_44px_-26px_rgba(22,163,74,0.55)] transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? 'Accepting...' : 'Accept'}
                  </button>
                </div>
              </div>
            </>
          ) : status === 'final-offer' && driverAmount ? (
            <>
              {/* Driver's Final Offer - Accept/Cancel Only */}
              <div className="rounded-[24px] border border-orange-300 bg-[linear-gradient(180deg,#fff5eb_0%,#ffffff_100%)] p-6 text-center shadow-[0_20px_50px_-36px_rgba(249,115,22,0.28)] dark:border-orange-500/30 dark:bg-[linear-gradient(180deg,#3b2216_0%,#111827_100%)]">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-white">
                  Driver's Final Offer
                </h3>
                <p className="text-4xl font-bold text-orange-600 mb-2 dark:text-orange-300">₹{driverAmount}</p>
                <p className="text-sm leading-7 text-gray-600 dark:text-slate-300">
                  This is the driver's final offer. Please accept or cancel the booking.
                </p>
              </div>

              <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.12)] dark:border-slate-700 dark:bg-slate-800">
                <div className="space-y-3">
                  <div className="rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Pickup</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">
                      {finalBooking?.pickupLocation || finalBooking?.pickupLocationName || booking?.pickupLocation || booking?.pickupLocationName || 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-[18px] border border-sky-100 bg-sky-50/70 p-3 dark:border-sky-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Drop-off</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">{finalBooking?.dropoffLocation || booking?.dropoffLocation}</p>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-1 mt-5 border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,251,255,0)_0%,rgba(248,251,255,0.96)_20%,#f8fbff_100%)] px-1 pb-1 pt-4 dark:border-slate-700 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0)_0%,rgba(15,23,42,0.96)_20%,#111827_100%)]">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleCancelNegotiation}
                    className="rounded-[18px] bg-red-500 px-6 py-3 text-white font-semibold transition-colors hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAcceptAmount}
                    className="rounded-[18px] bg-[linear-gradient(135deg,#16a34a_0%,#15803d_100%)] px-6 py-3 text-white font-semibold transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? 'Accepting...' : 'Accept'}
                  </button>
                </div>
              </div>
            </>
          ) : status === 'received' ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center dark:border-rose-500/30 dark:bg-slate-800">
              <p className="text-red-600 dark:text-rose-300">Waiting for driver's amount...</p>
              <p className="text-xs text-gray-500 mt-2 dark:text-slate-400">Debug: status={status}, driverAmount={driverAmount}</p>
            </div>
          ) : null}

          {/* Editing - Counter Offer */}
          {status === 'editing' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-[20px] p-4 mb-4 dark:border-blue-500/30 dark:bg-slate-800">
                <p className="text-sm text-gray-700 dark:text-slate-300">
                  <span className="font-semibold">Driver's offer:</span> ₹{driverAmount}
                </p>
              </div>

              <div>
                <label htmlFor="userAmount" className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Your Proposed Amount (₹) *
                </label>
                <input
                  type="number"
                  id="userAmount"
                  value={userAmount}
                  onChange={(e) => setUserAmount(e.target.value)}
                  placeholder="Enter your amount"
                  className="w-full px-4 py-3 border border-gray-300 rounded-[18px] focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-400"
                  min="0"
                  step="10"
                  disabled={loading}
                  required
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Propose a fair amount for this trip</p>
              </div>

              <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.12)] dark:border-slate-700 dark:bg-slate-800">
                <div className="space-y-3">
                  <div className="rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Pickup</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">
                      {finalBooking?.pickupLocation || finalBooking?.pickupLocationName || booking?.pickupLocation || booking?.pickupLocationName || 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-[18px] border border-sky-100 bg-sky-50/70 p-3 dark:border-sky-500/20 dark:bg-slate-700">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Drop-off</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">{finalBooking?.dropoffLocation || booking?.dropoffLocation}</p>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 -mx-1 mt-5 border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,251,255,0)_0%,rgba(248,251,255,0.96)_20%,#f8fbff_100%)] px-1 pb-1 pt-4 dark:border-slate-700 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0)_0%,rgba(15,23,42,0.96)_20%,#111827_100%)]">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setStatus('received')}
                    className="rounded-[18px] bg-gray-200 px-6 py-3 text-gray-800 font-semibold transition-colors hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendCounterOffer}
                    className="rounded-[18px] bg-[linear-gradient(135deg,#2563eb_0%,#1d4ed8_100%)] px-6 py-3 text-white font-semibold transition-all hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || !userAmount}
                  >
                    {loading ? 'Sending...' : 'Send to Driver'}
                  </button>
                </div>
              </div>
            </>
          )}

          {!['received', 'final-offer', 'editing'].includes(status) && (
            <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)] p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.12)] dark:border-slate-700 dark:bg-slate-800">
            <div className="space-y-3">
              <div className="flex items-start space-x-2 rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-500/20 dark:bg-slate-700">
                <span className="text-green-600 font-semibold">ðŸ“</span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Pickup</p>
                  {/* Priority: pickupLocation (Google Places address from "Location Address" input) > pickupLocationName (pincode search from "Location Name" input) */}
                  <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">
                    {(() => {
                      const displayValue = finalBooking?.pickupLocation || finalBooking?.pickupLocationName || booking?.pickupLocation || booking?.pickupLocationName || 'N/A';
                      console.log('ðŸ“ NegotiationPopup - Displaying Pickup Location:', {
                        finalBookingPickupLocation: finalBooking?.pickupLocation,
                        finalBookingPickupLocationName: finalBooking?.pickupLocationName,
                        bookingPickupLocation: booking?.pickupLocation,
                        bookingPickupLocationName: booking?.pickupLocationName,
                        displayValue: displayValue,
                        source: (finalBooking?.pickupLocation || booking?.pickupLocation) ? 'pickupLocation (Google Places)' : 'pickupLocationName (pincode search)'
                      });
                      return displayValue;
                    })()}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 rounded-[18px] border border-sky-100 bg-sky-50/70 p-3 dark:border-sky-500/20 dark:bg-slate-700">
                <span className="text-red-600 font-semibold">ðŸ“</span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Drop-off</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">{finalBooking?.dropoffLocation || booking?.dropoffLocation}</p>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* User Waiting for Driver Response */}
          {status === 'user-waiting' && (
            <div className="rounded-[24px] border border-blue-100 bg-blue-50/60 text-center py-8 dark:border-blue-500/30 dark:bg-slate-800">
              <div className="animate-pulse mb-4">
                <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-white">
                Waiting for Driver Response...
              </h3>
              <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">
                You proposed: <span className="font-bold text-blue-600 dark:text-blue-300">₹{userAmount}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                The driver is reviewing your counter offer
              </p>
            </div>
          )}

          {/* Cancelled State */}
          {status === 'cancelled' && (
            <div className="bg-red-50 border border-red-200 rounded-[24px] p-6 text-center dark:border-rose-500/30 dark:bg-slate-800">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-red-800 mb-2 dark:text-rose-200">
                Booking Cancelled
              </h3>
              <p className="text-gray-700 mb-4 dark:text-slate-300">
                This booking has been cancelled. You can book a new ride anytime.
              </p>
              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-[18px] font-semibold transition-all dark:bg-slate-700 dark:hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          )}

          {/* Confirmed State */}
          {status === 'confirmed' && otp && (
            <div className="bg-[linear-gradient(180deg,#f0fdf4_0%,#ffffff_100%)] border border-green-200 rounded-[24px] p-6 text-center dark:border-emerald-500/30 dark:bg-[linear-gradient(180deg,#052e24_0%,#111827_100%)]">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-2 dark:text-emerald-200">
                Booking Confirmed!
              </h3>
              <p className="text-lg text-gray-700 mb-4 dark:text-slate-300">
                Final Fare: <span className="font-bold text-green-600 dark:text-emerald-300">₹{driverAmount}</span>
              </p>

              {/* Reserve Booking Schedule in Confirmed State */}
              {(finalBooking?.bookingType || booking?.bookingType) === 'reserve' && ((finalBooking?.scheduledStartDate || booking?.scheduledStartDate) || (finalBooking?.scheduledStartTime || booking?.scheduledStartTime)) && (
                <div className="bg-purple-50 border-2 border-purple-300 rounded-[20px] p-4 mb-4 text-left dark:border-purple-500/30 dark:bg-slate-800">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h4 className="text-sm font-bold text-purple-900 dark:text-purple-200">📅 Reserve Booking Schedule</h4>
                  </div>
                  <div className="space-y-1">
                    {(finalBooking?.scheduledStartDate || booking?.scheduledStartDate) && (
                      <p className="text-xs text-gray-700 dark:text-slate-300">
                        <span className="font-semibold">Date:</span>{' '}
                        {new Date(finalBooking?.scheduledStartDate || booking.scheduledStartDate).toLocaleDateString('en-IN', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                    {(finalBooking?.scheduledStartTime || booking?.scheduledStartTime) && (
                      <p className="text-xs text-gray-700 dark:text-slate-300">
                        <span className="font-semibold">Time:</span> {finalBooking?.scheduledStartTime || booking.scheduledStartTime}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              <div className="bg-white border-2 border-green-500 rounded-[20px] p-4 mb-4 dark:border-emerald-500/40 dark:bg-slate-900">
                <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Your Pickup OTP</p>
                <p className="text-3xl font-bold text-green-600 tracking-wider dark:text-emerald-300">
                  {extractOtpCode(otp) || 'N/A'}
                </p>
              </div>
              
              <p className="text-xs text-gray-500 mb-4 dark:text-slate-400">
                Share this OTP with the driver at pickup for verification
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-[20px] p-3 mb-4 text-left dark:border-blue-500/30 dark:bg-slate-800">
                <p className="text-xs font-semibold text-blue-900 mb-2 dark:text-blue-300">Next Steps:</p>
                <ol className="text-xs text-gray-700 space-y-1 list-decimal list-inside dark:text-slate-300">
                  {(finalBooking?.bookingType || booking?.bookingType) === 'reserve' ? (
                    <>
                      <li>Your ride is scheduled for the date and time shown above</li>
                      <li>Driver will arrive at the scheduled time</li>
                      <li>Share the OTP when driver arrives</li>
                      <li>Enjoy your ride!</li>
                    </>
                  ) : (
                    <>
                      <li>Driver is on the way to your pickup location</li>
                      <li>Share the OTP when driver arrives</li>
                      <li>Enjoy your ride!</li>
                    </>
                  )}
                </ol>
              </div>

              <button
                onClick={() => {
                  console.log('🚪 Got It button clicked - closing popup permanently');
                  // Don't call onConfirmed again - it was already called when user accepted
                  // Just close the popup
                  if (onClose) {
                    onClose();
                  }
                }}
                className="w-full px-6 py-3 bg-[linear-gradient(135deg,#16a34a_0%,#15803d_100%)] hover:brightness-105 text-white rounded-[18px] font-semibold transition-all"
              >
                Got It!
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
      
      {/* Message Modal */}
      {messageModal.show && (
        <MessageModal
          message={messageModal.message}
          title={messageModal.title}
          type={messageModal.type}
          onClose={() => setMessageModal({ ...messageModal, show: false })}
        />
      )}
    </div>
  );
};

export default NegotiationPopup;

